// INVXFER1-S2 (docs/build-briefs/INVXFER1-stock-transfers-between-bars.md section 9; design/mockups/invxfer1-s2/): the
// stock transfer screens, drawn into one panel of the admin shell: the list, New transfer, Receive and the detail.
// ui/src/transfersModel.ts holds every rule; this module only draws it and reports taps to the API.
//
// Every control is a button, a chip or a stepper: no typed quantity and no free text. Each control reads its own
// permission id (inv.transfer.view, .send, .receive); the server checks every route again. Text is set with
// textContent only (item and location names are user-entered). A send, receive or cancel carries one idempotency key
// that is kept across a failed attempt and replaced as soon as the body changes.

import {
  cancelTransfer,
  getTransferDetail,
  listLiquorProducts,
  listTransferLocations,
  listTransfers,
  receiveTransfer,
  sendTransfer,
  type TransferRowDto,
  type TransferViewDto,
} from "./api.ts";
import { capitalizeServerMessage } from "./serverMessageDisplay.ts";
import { hasPermission, type Session } from "./session.ts";
import {
  EXCEPTION_REASONS,
  NOT_AVAILABLE,
  TRANSFER_FILTERS,
  addSendLine,
  locationName,
  canSend,
  canStartTransfer,
  canStep,
  dateTimeText,
  destinationLocations,
  detailActions,
  detailItemsTitle,
  detailLines,
  detailStatus,
  filterCount,
  filterTransfers,
  lineDisplay,
  pluralUnit,
  quantityWithUnit,
  receiveBody,
  removeSendLine,
  sendBody,
  sendButtonLabel,
  sendTitle,
  sendableItems,
  shortDateText,
  shortRowText,
  shortRows,
  shortTitle,
  startReceive,
  stepReceiveLine,
  stepSendLine,
  thisLocation,
  transferGates,
  transferRowView,
  type LocationDto,
  type ReceiveLineState,
  type SendLine,
  type SendableItem,
  type StepKind,
  type TransferFilter,
} from "./transfersModel.ts";

export interface TransfersHooks {
  /** The list's back button: back to the Inventory hub. */
  exit: () => void;
  toast: (message: string, tone: "success" | "danger") => void;
}

type Screen =
  | { kind: "list" }
  | { kind: "new" }
  | { kind: "detail"; id: string }
  | { kind: "receive"; id: string };

interface State {
  host: HTMLElement;
  session: Session;
  hooks: TransfersHooks;
  screen: Screen;
  /** Bumped on every open, reset and screen change; a late response for an older one writes nothing. */
  epoch: number;
  locations: LocationDto[] | undefined;
  rows: TransferRowDto[] | undefined;
  listError: string | undefined;
  filter: TransferFilter;
  items: SendableItem[] | undefined;
  itemsFailed: boolean;
  destinationId: string | undefined;
  lines: SendLine[];
  detail: TransferViewDto | undefined;
  detailError: string | undefined;
  receive: ReceiveLineState[];
  reason: string | undefined;
  confirmCancel: boolean;
  busy: boolean;
  error: string | undefined;
  /** The key of the attempt in flight or just failed; cleared when the body changes. */
  key: string | undefined;
}

let state: State | undefined;

/** Sign-out and every other invalidate: nothing of the last person's transfers stays in memory. */
export function resetTransfers(): void {
  if (state !== undefined) {
    state.epoch += 1;
    state.host.replaceChildren();
  }
  state = undefined;
}

export function refreshTransfersSession(session: Session): void {
  if (state === undefined) return;
  state.session = session;
  render();
}

export async function openTransfers(
  host: HTMLElement,
  session: Session,
  hooks: TransfersHooks,
): Promise<void> {
  state = {
    host,
    session,
    hooks,
    screen: { kind: "list" },
    epoch: (state?.epoch ?? 0) + 1,
    locations: undefined,
    rows: undefined,
    listError: undefined,
    filter: "all",
    items: undefined,
    itemsFailed: false,
    destinationId: undefined,
    lines: [],
    detail: undefined,
    detailError: undefined,
    receive: [],
    reason: undefined,
    confirmCancel: false,
    busy: false,
    error: undefined,
    key: undefined,
  };
  render();
  await loadList();
}

// ---------------------------------------------------------------------
// Loading
// ---------------------------------------------------------------------

function current(epoch: number): State | undefined {
  return state !== undefined && state.epoch === epoch ? state : undefined;
}

function failure(lead: string, body: { message?: string; error?: string } | undefined): string {
  const detail = body?.message ?? body?.error;
  return detail === undefined ? lead : `${lead} ${capitalizeServerMessage(detail)}`;
}

async function loadList(): Promise<void> {
  const s0 = state;
  if (s0 === undefined) return;
  const epoch = s0.epoch;
  const { apiBaseUrl, tenantId } = s0.session;
  const [locations, rows] = await Promise.all([
    listTransferLocations(apiBaseUrl, tenantId),
    listTransfers(apiBaseUrl, tenantId),
  ]);
  const s = current(epoch);
  if (s === undefined) return;
  s.locations = locations.status === 200 ? locations.body.locations : [];
  if (rows.status === 200) {
    s.rows = rows.body.transfers;
    s.listError = undefined;
  } else {
    s.rows = undefined;
    s.listError = failure("Could not load transfers.", rows.body);
  }
  render();
}

async function loadItems(): Promise<void> {
  const s0 = state;
  if (s0 === undefined) return;
  const epoch = s0.epoch;
  if (!hasPermission(s0.session, "admin.liquor_product.view")) {
    s0.items = undefined;
    s0.itemsFailed = true;
    render();
    return;
  }
  const result = await listLiquorProducts(s0.session.apiBaseUrl, s0.session.tenantId, {
    activeOnly: true,
  });
  const s = current(epoch);
  if (s === undefined) return;
  if (result.status === 200) {
    s.items = sendableItems(result.body.liquorProducts);
    s.itemsFailed = false;
  } else {
    s.items = undefined;
    s.itemsFailed = true;
  }
  render();
}

async function loadDetail(id: string, forReceive: boolean): Promise<void> {
  const s0 = state;
  if (s0 === undefined) return;
  const epoch = s0.epoch;
  const result = await getTransferDetail(s0.session.apiBaseUrl, s0.session.tenantId, id);
  const s = current(epoch);
  if (s === undefined) return;
  if (result.status === 200) {
    s.detail = result.body.transfer;
    s.detailError = undefined;
    if (forReceive) s.receive = startReceive(result.body.transfer.lines);
  } else {
    s.detail = undefined;
    s.detailError = failure("Could not load this transfer.", result.body);
  }
  render();
}

// ---------------------------------------------------------------------
// Navigation
// ---------------------------------------------------------------------

function go(screen: Screen): void {
  const s = state;
  if (s === undefined) return;
  s.epoch += 1;
  s.screen = screen;
  s.busy = false;
  s.error = undefined;
  s.key = undefined;
  s.confirmCancel = false;
  s.reason = undefined;
  if (screen.kind === "list") {
    render();
    void loadList();
  } else if (screen.kind === "new") {
    s.lines = [];
    s.destinationId = undefined;
    s.items = undefined;
    s.itemsFailed = false;
    const others = destinationLocations(s.locations ?? []);
    if (others.length === 1) s.destinationId = others[0]?.id;
    render();
    void loadItems();
  } else {
    s.detail = undefined;
    s.detailError = undefined;
    s.receive = [];
    render();
    void loadDetail(screen.id, screen.kind === "receive");
  }
}

/** The key for the next attempt: made on the first tap, kept while the same body is retried. */
function attemptKey(s: State): string {
  if (s.key === undefined) s.key = crypto.randomUUID();
  return s.key;
}

function bodyChanged(s: State): void {
  s.key = undefined;
  s.error = undefined;
}

// ---------------------------------------------------------------------
// DOM helpers
// ---------------------------------------------------------------------

interface Opts {
  cls?: string;
  text?: string;
  testid?: string;
  attrs?: Record<string, string>;
}

function h(tag: string, opts: Opts = {}, children: Array<Node | string> = []): HTMLElement {
  const element = document.createElement(tag);
  if (opts.cls !== undefined) element.className = opts.cls;
  if (opts.text !== undefined) element.textContent = opts.text;
  if (opts.testid !== undefined) element.dataset["testid"] = opts.testid;
  for (const [key, value] of Object.entries(opts.attrs ?? {})) element.setAttribute(key, value);
  for (const child of children) element.append(child);
  return element;
}

function button(
  text: string,
  cls: string,
  testid: string,
  onClick: () => void,
  options: { disabled?: boolean; pressed?: boolean; label?: string } = {},
): HTMLButtonElement {
  const element = h("button", {
    cls,
    text,
    testid,
    attrs: { type: "button" },
  }) as HTMLButtonElement;
  if (options.pressed !== undefined) element.setAttribute("aria-pressed", String(options.pressed));
  if (options.label !== undefined) element.setAttribute("aria-label", options.label);
  if (options.disabled === true) {
    element.disabled = true;
  } else {
    element.addEventListener("click", onClick);
  }
  return element;
}

function chip(
  label: string,
  on: boolean,
  testid: string,
  onClick: () => void,
  count?: number,
): HTMLElement {
  const element = button("", `xfer-chip${on ? " xfer-on" : ""}`, testid, onClick, { pressed: on });
  element.append(label);
  if (count !== undefined) element.append(" ", h("b", { text: String(count) }));
  return element;
}

function header(eyebrow: string, title: string, back: () => void, right?: Node): HTMLElement {
  const left = h("div", { cls: "xfer-head-left" }, [
    button("‹", "xfer-back", "transfers-back", back, { label: "Back" }),
    h("div", {}, [
      h("p", { cls: "xfer-eyebrow", text: eyebrow, testid: "transfers-eyebrow" }),
      h("h1", { cls: "xfer-title", text: title, testid: "transfers-title" }),
    ]),
  ]);
  return h("div", { cls: "xfer-head" }, right === undefined ? [left] : [left, right]);
}

function pill(label: string, tone: string): HTMLElement {
  return h("span", {
    cls: `xfer-pill xfer-pill-${tone}`,
    text: label,
    testid: "transfer-state-pill",
  });
}

function errorLine(s: State): HTMLElement | undefined {
  return s.error === undefined
    ? undefined
    : h("p", {
        cls: "xfer-error",
        text: s.error,
        testid: "transfer-error",
        attrs: { role: "alert" },
      });
}

function stepper(
  kind: string,
  value: string,
  min: string,
  max: string,
  unit: string,
  onStep: (kind: StepKind) => void,
): HTMLElement {
  const down = button("−", "xfer-step", `transfer-step-down-${kind}`, () => onStep("down"), {
    disabled: !canStep(value, "down", min, max),
    label: "Less",
  });
  const up = button("+", "xfer-step", `transfer-step-up-${kind}`, () => onStep("up"), {
    disabled: !canStep(value, "up", min, max),
    label: "More",
  });
  const out = h("output", { text: value, testid: `transfer-qty-${kind}` });
  const whole = button("+1", "xfer-whole", `transfer-step-whole-${kind}`, () => onStep("whole"), {
    disabled: !canStep(value, "whole", min, max),
    label: "Add one",
  });
  return h("div", { cls: "xfer-stepper-wrap" }, [
    h("span", { cls: "xfer-stepper" }, [down, out, up]),
    h("span", { cls: "xfer-unit", text: unit }),
    whole,
  ]);
}

// ---------------------------------------------------------------------
// Screens
// ---------------------------------------------------------------------

function render(): void {
  const s = state;
  if (s === undefined) return;
  const gates = transferGates((id) => hasPermission(s.session, id));
  let screen: HTMLElement;
  if (!gates.view) {
    screen = h("p", {
      cls: "xfer-empty",
      text: "You do not have permission to use this screen.",
      testid: "transfers-denied",
    });
  } else if (s.screen.kind === "list") {
    screen = listScreen(s, gates);
  } else if (s.screen.kind === "new") {
    screen = newScreen(s, gates);
  } else if (s.screen.kind === "receive") {
    screen = receiveScreen(s, gates, s.screen.id);
  } else {
    screen = detailScreen(s, gates, s.screen.id);
  }
  const root = h(
    "div",
    { cls: "xfer", testid: `transfers-screen-${gates.view ? s.screen.kind : "denied"}` },
    [screen],
  );
  s.host.replaceChildren(root);
}

function listScreen(s: State, gates: ReturnType<typeof transferGates>): HTMLElement {
  const locations = s.locations ?? [];
  const newButton = gates.send
    ? button(
        "+ New transfer",
        "xfer-btn xfer-btn-primary",
        "transfers-new",
        () => go({ kind: "new" }),
        { disabled: !canStartTransfer(gates, locations) },
      )
    : undefined;
  const parts: Array<Node | undefined> = [
    header("Inventory", "Transfers", s.hooks.exit, newButton),
  ];
  const rows = s.rows;
  parts.push(
    h(
      "div",
      { cls: "xfer-chips", testid: "transfers-filters" },
      TRANSFER_FILTERS.map((filter) =>
        chip(
          filter.label,
          s.filter === filter.id,
          `transfers-filter-${filter.id}`,
          () => {
            s.filter = filter.id;
            render();
          },
          rows === undefined ? undefined : filterCount(rows, filter.id),
        ),
      ),
    ),
  );
  if (s.listError !== undefined) {
    parts.push(h("p", { cls: "xfer-error", text: s.listError, testid: "transfers-list-error" }));
  } else if (rows === undefined) {
    parts.push(h("p", { cls: "xfer-empty", text: "Loading…", testid: "transfers-loading" }));
  } else {
    const shown = filterTransfers(rows, s.filter);
    if (shown.length === 0) {
      parts.push(h("p", { cls: "xfer-empty", text: NOT_AVAILABLE, testid: "transfers-empty" }));
    } else {
      parts.push(
        h(
          "div",
          { cls: "xfer-card xfer-rows", testid: "transfers-list" },
          shown.map((row) => {
            const view = transferRowView(row, locations);
            const element = h(
              "button",
              { cls: "xfer-trow", testid: "transfer-row", attrs: { type: "button" } },
              [
                pill(view.stateLabel, view.state),
                h("span", { cls: "xfer-route" }, [
                  view.from,
                  h("span", { cls: "xfer-arrow", text: "→", attrs: { "aria-hidden": "true" } }),
                  view.to,
                ]),
                h("span", { cls: "xfer-when", text: view.when }),
                h("span", { cls: "xfer-count", text: view.items }),
                h("span", { cls: "xfer-go", text: "›", attrs: { "aria-hidden": "true" } }),
              ],
            );
            element.addEventListener("click", () => go({ kind: "detail", id: row.id }));
            return element;
          }),
        ),
      );
    }
  }
  return h(
    "div",
    { cls: "xfer-page" },
    parts.filter((part): part is Node => part !== undefined),
  );
}

function newScreen(s: State, gates: ReturnType<typeof transferGates>): HTMLElement {
  const back = (): void => go({ kind: "list" });
  const locations = s.locations ?? [];
  if (!gates.send) {
    return h("div", { cls: "xfer-page" }, [
      header("Inventory", "New transfer", back),
      h("p", { cls: "xfer-empty", text: "You do not have permission to send a transfer." }),
    ]);
  }
  const others = destinationLocations(locations);
  const destination = others.find((siteLocation) => siteLocation.id === s.destinationId);
  const toRow = h("div", { cls: "xfer-to" }, [
    h("span", { cls: "xfer-k", text: "To" }),
    h(
      "div",
      { cls: "xfer-chips", testid: "transfer-destinations" },
      others.map((siteLocation) =>
        chip(
          siteLocation.name,
          siteLocation.id === s.destinationId,
          `transfer-dest-${siteLocation.id}`,
          () => {
            s.destinationId = siteLocation.id;
            bodyChanged(s);
            render();
          },
        ),
      ),
    ),
  ]);

  const addedIds = new Set(s.lines.map((line) => line.item.id));
  let addBody: Node;
  if (s.itemsFailed) {
    addBody = h("p", { cls: "xfer-empty", text: NOT_AVAILABLE, testid: "transfer-items-na" });
  } else if (s.items === undefined) {
    addBody = h("p", { cls: "xfer-empty", text: "Loading…" });
  } else {
    const available = s.items.filter((item) => !addedIds.has(item.id));
    addBody =
      available.length === 0
        ? h("p", { cls: "xfer-empty", text: NOT_AVAILABLE, testid: "transfer-items-empty" })
        : h(
            "div",
            { cls: "xfer-lines", testid: "transfer-items" },
            available.map((item) =>
              h("div", { cls: "xfer-ln", testid: "transfer-item" }, [
                h("div", {}, [
                  h("div", { cls: "xfer-n", text: item.name }),
                  h("div", {
                    cls: "xfer-s",
                    text: `${quantityWithUnit(item.onHand, item.unit)} on hand`,
                  }),
                ]),
                button("+ Add", "xfer-btn xfer-btn-small", `transfer-add-${item.id}`, () => {
                  s.lines = addSendLine(s.lines, item);
                  bodyChanged(s);
                  render();
                }),
              ]),
            ),
          );
  }
  const addCard = h("div", { cls: "xfer-card xfer-card-scroll" }, [
    h("div", { cls: "xfer-sectitle xfer-k", text: "Add items" }),
    addBody,
  ]);

  const lineRows = s.lines.map((line) => {
    const min = "0.1";
    return h("div", { cls: "xfer-ln", testid: "transfer-line" }, [
      h("div", {}, [
        h("div", { cls: "xfer-n", text: line.item.name }),
        h("div", { cls: "xfer-s", text: `of ${line.item.onHand} on hand` }),
      ]),
      stepper(
        line.item.id,
        line.quantity,
        min,
        line.item.onHand,
        pluralUnit(line.item.unit),
        (kind) => {
          s.lines = s.lines.map((entry) =>
            entry.item.id === line.item.id ? stepSendLine(entry, kind) : entry,
          );
          bodyChanged(s);
          render();
        },
      ),
      button(
        "×",
        "xfer-remove",
        `transfer-remove-${line.item.id}`,
        () => {
          s.lines = removeSendLine(s.lines, line.item.id);
          bodyChanged(s);
          render();
        },
        { label: `Remove ${line.item.name}` },
      ),
    ]);
  });
  const sendDisabled = s.busy || !canSend(s.lines, destination);
  const send = button(
    sendButtonLabel(destination),
    "xfer-btn xfer-btn-primary xfer-btn-wide",
    "transfer-send",
    () => void doSend(),
    { disabled: sendDisabled },
  );
  const sendCard = h("div", { cls: "xfer-card xfer-card-col" }, [
    h("div", {
      cls: "xfer-sectitle xfer-k",
      text: sendTitle(s.lines.length),
      testid: "transfer-send-title",
    }),
    h("div", { cls: "xfer-lines xfer-grow", testid: "transfer-lines" }, lineRows),
    h("div", { cls: "xfer-foot" }, [errorLine(s) ?? h("span"), send]),
  ]);
  return h("div", { cls: "xfer-page" }, [
    header("Inventory", "New transfer", back),
    toRow,
    h("div", { cls: "xfer-two" }, [addCard, sendCard]),
  ]);
}

async function doSend(): Promise<void> {
  const s = state;
  if (s === undefined || s.busy) return;
  const destination = destinationLocations(s.locations ?? []).find(
    (siteLocation) => siteLocation.id === s.destinationId,
  );
  if (!canSend(s.lines, destination) || destination === undefined) return;
  s.busy = true;
  s.error = undefined;
  render();
  const epoch = s.epoch;
  const body = sendBody(s.lines, destination, attemptKey(s));
  const result = await sendTransfer(s.session.apiBaseUrl, s.session.tenantId, body);
  const live = current(epoch);
  if (live === undefined) return;
  live.busy = false;
  if (result.status === 200 || result.status === 201) {
    live.hooks.toast(`Sent to ${destination.name}`, "success");
    go({ kind: "detail", id: result.body.transfer.transfer.id });
    return;
  }
  live.error = failure("Not sent.", result.body);
  render();
}

function receiveScreen(s: State, gates: ReturnType<typeof transferGates>, id: string): HTMLElement {
  const back = (): void => go({ kind: "detail", id });
  const detail = s.detail;
  if (detail === undefined) {
    return h("div", { cls: "xfer-page" }, [
      header("Inventory", "Receive", () => go({ kind: "list" })),
      h("p", {
        cls: s.detailError === undefined ? "xfer-empty" : "xfer-error",
        text: s.detailError ?? "Loading…",
        testid: "transfer-detail-status",
      }),
    ]);
  }
  const locations = s.locations ?? [];
  const actions = detailActions(detail.transfer, thisLocation(locations)?.id, gates);
  const eyebrow = `From ${locationName(locations, detail.transfer.from_location_id)}, ${shortDateText(detail.transfer.sent_at)}`;
  if (!actions.canReceive) {
    return h("div", { cls: "xfer-page" }, [
      header(eyebrow, "Receive", back),
      h("p", { cls: "xfer-empty", text: NOT_AVAILABLE, testid: "transfer-receive-na" }),
    ]);
  }
  const lineRows = s.receive.map((entry, index) => {
    const display = lineDisplay(entry.line);
    return h("div", { cls: "xfer-ln", testid: "transfer-receive-line" }, [
      h("div", {}, [
        h("div", { cls: "xfer-n", text: entry.line.componentReference }),
        h("div", {
          cls: "xfer-s",
          text: `Sent ${quantityWithUnit(display.quantity, display.unit)}`,
        }),
      ]),
      stepper(
        entry.line.id,
        entry.received,
        "0",
        display.quantity,
        pluralUnit(display.unit),
        (kind) => {
          const next = s.receive.slice();
          next[index] = stepReceiveLine(entry, kind);
          s.receive = next;
          bodyChanged(s);
          render();
        },
      ),
    ]);
  });
  const short = shortRows(s.receive);
  const rightParts: Node[] = [];
  if (short.length === 0) {
    rightParts.push(
      h("div", {
        cls: "xfer-sectitle xfer-k",
        text: "All received",
        testid: "transfer-short-title",
      }),
    );
  } else {
    rightParts.push(
      h("div", {
        cls: "xfer-sectitle xfer-k",
        text: shortTitle(short.length),
        testid: "transfer-short-title",
      }),
      h(
        "div",
        { cls: "xfer-short-list" },
        short.map((row) =>
          h("p", { cls: "xfer-short", text: shortRowText(row), testid: "transfer-short-row" }),
        ),
      ),
      h("div", { cls: "xfer-sectitle xfer-k", text: "Reason (optional)" }),
      h(
        "div",
        { cls: "xfer-grid2", testid: "transfer-reasons" },
        EXCEPTION_REASONS.map((reason) =>
          button(
            reason,
            `xfer-opt${s.reason === reason ? " xfer-on" : ""}`,
            `transfer-reason-${reason.toLowerCase().replace(/\s+/g, "-")}`,
            () => {
              s.reason = s.reason === reason ? undefined : reason;
              bodyChanged(s);
              render();
            },
            { pressed: s.reason === reason },
          ),
        ),
      ),
    );
  }
  rightParts.push(
    h("div", { cls: "xfer-grow" }),
    errorLine(s) ?? h("span"),
    button(
      "Receive",
      "xfer-btn xfer-btn-primary xfer-btn-wide",
      "transfer-receive-submit",
      () => void doReceive(id),
      {
        disabled: s.busy,
      },
    ),
  );
  return h("div", { cls: "xfer-page" }, [
    header(eyebrow, "Receive", back),
    h("div", { cls: "xfer-two xfer-two-receive" }, [
      h("div", { cls: "xfer-card" }, [
        h("div", {
          cls: "xfer-sectitle xfer-k",
          text: `Sent ${s.receive.length} ${s.receive.length === 1 ? "item" : "items"}`,
        }),
        h("div", { cls: "xfer-lines", testid: "transfer-receive-lines" }, lineRows),
      ]),
      h("div", { cls: "xfer-card xfer-card-col xfer-pad" }, rightParts),
    ]),
  ]);
}

async function doReceive(id: string): Promise<void> {
  const s = state;
  if (s === undefined || s.busy) return;
  s.busy = true;
  s.error = undefined;
  render();
  const epoch = s.epoch;
  const body = receiveBody(s.receive, s.reason, attemptKey(s));
  const result = await receiveTransfer(s.session.apiBaseUrl, s.session.tenantId, id, body);
  const live = current(epoch);
  if (live === undefined) return;
  live.busy = false;
  if (result.status === 200 || result.status === 201) {
    live.hooks.toast("Received", "success");
    go({ kind: "detail", id });
    return;
  }
  live.error = failure("Not received.", result.body);
  render();
}

function detailScreen(s: State, gates: ReturnType<typeof transferGates>, id: string): HTMLElement {
  const back = (): void => go({ kind: "list" });
  const detail = s.detail;
  if (detail === undefined) {
    return h("div", { cls: "xfer-page" }, [
      header("Inventory", "Transfer", back),
      h("p", {
        cls: s.detailError === undefined ? "xfer-empty" : "xfer-error",
        text: s.detailError ?? "Loading…",
        testid: "transfer-detail-status",
      }),
    ]);
  }
  const locations = s.locations ?? [];
  const row = detail.transfer;
  const view = transferRowView(row, locations);
  const lines = detailLines(detail.lines);
  const shortCount = detail.lines.filter((line) => line.exception !== null).length;
  const actions = detailActions(row, thisLocation(locations)?.id, gates);
  const lineRows = lines.map((line) =>
    h("div", { cls: "xfer-ln", testid: "transfer-detail-line" }, [
      h("div", {}, [
        h("div", { cls: "xfer-n", text: line.name }),
        line.note === undefined ? h("span") : h("div", { cls: "xfer-s", text: line.note }),
      ]),
      h("div", {
        cls: "xfer-qty",
        text: line.received === undefined ? line.sent : `${line.received} of ${line.sent}`,
      }),
    ]),
  );
  const side: Node[] = [
    h("div", { cls: "xfer-k", text: "Sent by" }),
    h("div", {
      cls: "xfer-v",
      text: row.sent_by_display_name ?? NOT_AVAILABLE,
      testid: "transfer-sent-by",
    }),
    h("div", { cls: "xfer-k", text: "Status" }),
    h("div", {
      cls: "xfer-v",
      text: detailStatus(row.state, view.to, shortCount),
      testid: "transfer-status-text",
    }),
    h("div", { cls: "xfer-grow" }),
  ];
  const err = errorLine(s);
  if (err !== undefined) side.push(err);
  if (actions.canReceive) {
    side.push(
      button("Receive", "xfer-btn xfer-btn-primary xfer-btn-wide", "transfer-receive-open", () =>
        go({ kind: "receive", id }),
      ),
    );
  }
  if (actions.canCancel) {
    if (s.confirmCancel) {
      side.push(
        h("p", {
          cls: "xfer-confirm",
          text: `Cancel this transfer? Stock goes back to ${view.from}.`,
          testid: "transfer-cancel-prompt",
        }),
        h("div", { cls: "xfer-grid2" }, [
          button("Keep it", "xfer-btn", "transfer-cancel-keep", () => {
            s.confirmCancel = false;
            render();
          }),
          button(
            "Cancel transfer",
            "xfer-btn xfer-btn-danger",
            "transfer-cancel-confirm",
            () => void doCancel(id),
            {
              disabled: s.busy,
            },
          ),
        ]),
      );
    } else {
      side.push(
        button(
          "Cancel transfer",
          "xfer-btn xfer-btn-danger xfer-btn-wide",
          "transfer-cancel",
          () => {
            s.confirmCancel = true;
            s.error = undefined;
            render();
          },
        ),
      );
    }
  }
  const right = h("span", {}, [pill(view.stateLabel, view.state)]);
  return h("div", { cls: "xfer-page" }, [
    header(dateTimeText(row.sent_at), `${view.from} → ${view.to}`, back, right),
    h("div", { cls: "xfer-two xfer-two-detail" }, [
      h("div", { cls: "xfer-card" }, [
        h("div", { cls: "xfer-sectitle xfer-k", text: detailItemsTitle(lines.length) }),
        h("div", { cls: "xfer-lines", testid: "transfer-detail-lines" }, lineRows),
      ]),
      h("div", { cls: "xfer-card xfer-card-col xfer-pad" }, side),
    ]),
  ]);
}

async function doCancel(id: string): Promise<void> {
  const s = state;
  if (s === undefined || s.busy) return;
  s.busy = true;
  s.error = undefined;
  render();
  const epoch = s.epoch;
  const result = await cancelTransfer(s.session.apiBaseUrl, s.session.tenantId, id, {
    idempotencyKey: attemptKey(s),
  });
  const live = current(epoch);
  if (live === undefined) return;
  live.busy = false;
  if (result.status === 200 || result.status === 201) {
    live.hooks.toast("Transfer cancelled", "success");
    live.confirmCancel = false;
    go({ kind: "detail", id });
    return;
  }
  live.confirmCancel = false;
  live.error = failure("Not cancelled.", result.body);
  render();
}
